// client/src/components/pages/CustomSummaries/FilterPanel.js
import React, { useState, useEffect } from 'react';
import { FaTimes, FaFilter, FaTrash } from 'react-icons/fa';
import ThemedSelect from '../../common/ThemedSelect';

const FilterPanel = ({ filters, onFilterChange, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Category options
  const categoryOptions = [
    'Operations',
    'Analytics',
    'Locations',
    'Financial',
    'Performance',
    'Custom',
  ];

  // Common tags
  const commonTags = [
    'daily',
    'weekly',
    'monthly',
    'performance',
    'routes',
    'locations',
    'weights',
    'variance',
    'trends',
    'dispatch',
    'receipt',
    'analysis',
  ];

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterUpdate = (field, value) => {
    const updatedFilters = { ...localFilters, [field]: value };
    setLocalFilters(updatedFilters);
  };

  const handleTagToggle = (tag) => {
    const currentTags = localFilters.tags || [];
    const updatedTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];

    handleFilterUpdate('tags', updatedTags);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    onClose();
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: '',
      category: '',
      tags: [],
      createdBy: '',
      isPublic: '',
      isFavorite: '',
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = () => {
    return (
      localFilters.search ||
      localFilters.category ||
      (localFilters.tags && localFilters.tags.length > 0) ||
      localFilters.createdBy ||
      localFilters.isPublic ||
      localFilters.isFavorite
    );
  };

  return (
    <div className="bg-surface border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <FaFilter className="text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters() && (
            <button
              onClick={handleClearFilters}
              className="flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800 transition-colors duration-200"
            >
              <FaTrash className="mr-1" />
              Clear All
            </button>
          )}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <ThemedSelect
            value={localFilters.category || ''}
            onChange={(e) => handleFilterUpdate('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Categories</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Created By Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Created By</label>
          <input
            type="text"
            value={localFilters.createdBy || ''}
            onChange={(e) => handleFilterUpdate('createdBy', e.target.value)}
            placeholder="Enter username or email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Visibility Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
          <ThemedSelect
            value={localFilters.isPublic || ''}
            onChange={(e) => handleFilterUpdate('isPublic', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Summaries</option>
            <option value="true">Public Only</option>
            <option value="false">Private Only</option>
          </ThemedSelect>
        </div>

        {/* Favorites Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Favorites</label>
          <ThemedSelect
            value={localFilters.isFavorite || ''}
            onChange={(e) => handleFilterUpdate('isFavorite', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Summaries</option>
            <option value="true">Favorites Only</option>
            <option value="false">Non-Favorites Only</option>
          </ThemedSelect>
        </div>
      </div>

      {/* Tags Filter */}
      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">Tags</label>
        <div className="flex flex-wrap gap-2">
          {commonTags.map((tag) => {
            const isSelected = localFilters.tags && localFilters.tags.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isSelected
                    ? 'bg-primary-100 text-primary-800 border border-primary-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                {tag}
              </button>
            );
          })}
        </div>

        {localFilters.tags && localFilters.tags.length > 0 && (
          <div className="mt-3">
            <div className="text-sm text-gray-600 mb-2">Selected tags:</div>
            <div className="flex flex-wrap gap-2">
              {localFilters.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
                >
                  {tag}
                  <button
                    onClick={() => handleTagToggle(tag)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    <FaTimes size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
        >
          Cancel
        </button>
        <button
          onClick={handleApplyFilters}
          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
        >
          Apply Filters
        </button>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters() && (
        <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="text-sm text-primary-800">
            <strong>Active Filters:</strong>
            <ul className="mt-1 space-y-1">
              {localFilters.search && <li>• Search: "{localFilters.search}"</li>}
              {localFilters.category && <li>• Category: {localFilters.category}</li>}
              {localFilters.tags && localFilters.tags.length > 0 && (
                <li>• Tags: {localFilters.tags.join(', ')}</li>
              )}
              {localFilters.createdBy && <li>• Created by: {localFilters.createdBy}</li>}
              {localFilters.isPublic && (
                <li>• Visibility: {localFilters.isPublic === 'true' ? 'Public' : 'Private'}</li>
              )}
              {localFilters.isFavorite && (
                <li>• Favorites: {localFilters.isFavorite === 'true' ? 'Yes' : 'No'}</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
